import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import redditApi from '../../services/redditApi';
import './reddit.css';

function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  // Invite validation
  const [invite, setInvite] = useState(null);   // { email, role, is_admin }
  const [tokenError, setTokenError] = useState('');
  const [validating, setValidating] = useState(true);

  // Form
  const [form, setForm] = useState({ first_name: '', last_name: '', password: '', confirm_password: '' });
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = 'Accept Invite – Reddit Dashboard';
    if (!token) {
      setTokenError('No invite token found. Please use the link from your email.');
      setValidating(false);
      return;
    }
    redditApi.get(`/invite/?token=${token}`)
      .then(({ data }) => { setInvite(data); })
      .catch((err) => {
        const detail = err.response?.data?.detail || 'This invite link is invalid or has expired.';
        setTokenError(detail);
      })
      .finally(() => setValidating(false));
  }, [token]);

  function set(field, value) { setForm((f) => ({ ...f, [field]: value })); }

  const pwMatch = form.confirm_password && form.password === form.confirm_password;
  const pwMismatch = form.confirm_password && form.password !== form.confirm_password;

  async function onSubmit(e) {
    e.preventDefault();
    if (pwMismatch) { setError('Passwords do not match.'); return; }
    setError('');
    setBusy(true);
    try {
      await redditApi.post('/invite/accept/', {
        token,
        first_name: form.first_name,
        last_name: form.last_name,
        password: form.password,
        confirm_password: form.confirm_password,
      });
      setDone(true);
      setTimeout(() => navigate('/reddit/login', { state: { invited: true } }), 3000);
    } catch (err) {
      const d = err.response?.data;
      setError(
        d?.detail ||
        d?.token?.[0] ||
        d?.confirm_password?.[0] ||
        d?.non_field_errors?.[0] ||
        'Something went wrong. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rh-auth-page">
      <div className="rh-auth-card" style={{ maxWidth: 460 }}>
        <div className="rh-auth-brand">
          <div className="rh-brand-icon">R</div>
          <div>
            <h1 className="rh-auth-title">Accept Invite</h1>
            <p className="rh-auth-subtitle">Reddit Campaign Hub</p>
          </div>
        </div>

        {validating && (
          <p style={{ textAlign: 'center', color: 'var(--rh-text-muted)', padding: '20px 0' }}>
            Validating invite…
          </p>
        )}

        {!validating && tokenError && (
          <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#fee2e2',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="24" height="24" fill="none" stroke="#dc2626" strokeWidth="2.5" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--rh-text)' }}>Invite not valid</p>
            <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--rh-text-muted)', lineHeight: 1.6 }}>
              {tokenError}
            </p>
            <Link to="/reddit/login" style={{ color: 'var(--rh-orange)', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
              ← Back to Sign in
            </Link>
          </div>
        )}

        {!validating && invite && !done && (
          <>
            {(() => {
              const roleStyles = {
                Admin:      { bg: 'rgba(99,102,241,0.08)',  border: 'rgba(99,102,241,0.25)',  pill: '#6366f1' },
                Accountant: { bg: 'rgba(37,99,235,0.07)',   border: 'rgba(37,99,235,0.22)',   pill: '#2563eb' },
                Redditor:   { bg: 'rgba(255,69,0,0.07)',    border: 'rgba(255,69,0,0.20)',    pill: 'var(--rh-orange)' },
              };
              const s = roleStyles[invite.role] || roleStyles.Redditor;
              return (
                <div className="rh-auth-invite-banner" style={{
                  background: s.bg, border: `1px solid ${s.border}`,
                  borderRadius: 10, padding: '14px 16px', marginBottom: 20,
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  {/* Left — role badge */}
                  <div style={{
                    background: s.pill, color: '#fff',
                    borderRadius: 7, padding: '4px 12px', fontSize: 11, fontWeight: 700,
                    flexShrink: 0, alignSelf: 'center', whiteSpace: 'nowrap',
                  }}>
                    {invite.role}
                  </div>

                  {/* Right — text lines */}
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: '0 0 3px', fontSize: 13, fontWeight: 600, color: 'var(--rh-text)', lineHeight: 1.4 }}>
                      You've been invited to Reddit Campaign Hub
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: 'var(--rh-text-muted)' }}>
                      This account will be created for <strong style={{ color: 'var(--rh-text)', overflowWrap: 'break-word', wordBreak: 'break-all' }}>{invite.email}</strong>
                    </p>
                  </div>
                </div>
              );
            })()}

            {error && <div className="rh-auth-error">{error}</div>}

            <form onSubmit={onSubmit} className="rh-auth-form">
              <div className="rh-auth-name-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="rh-field">
                  <label htmlFor="ai-fn">First name</label>
                  <input
                    id="ai-fn"
                    required
                    autoFocus
                    placeholder="Jane"
                    value={form.first_name}
                    onChange={(e) => set('first_name', e.target.value)}
                  />
                </div>
                <div className="rh-field">
                  <label htmlFor="ai-ln">Last name</label>
                  <input
                    id="ai-ln"
                    placeholder="Smith"
                    value={form.last_name}
                    onChange={(e) => set('last_name', e.target.value)}
                  />
                </div>
              </div>

              <div className="rh-field">
                <label htmlFor="ai-pw">Password</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    id="ai-pw"
                    type={showPw ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="Min. 8 characters"
                    value={form.password}
                    onChange={(e) => set('password', e.target.value)}
                    style={{ width: '100%', paddingRight: 42, boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    style={{
                      position: 'absolute', right: 12,
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--rh-text-muted)', display: 'flex', alignItems: 'center', padding: 0, lineHeight: 1,
                    }}
                    tabIndex={-1}
                    aria-label={showPw ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon open={showPw} />
                  </button>
                </div>
              </div>

              <div className="rh-field">
                <label htmlFor="ai-cpw">Confirm password</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <input
                    id="ai-cpw"
                    type={showConfirm ? 'text' : 'password'}
                    required
                    minLength={8}
                    placeholder="Repeat password"
                    value={form.confirm_password}
                    onChange={(e) => set('confirm_password', e.target.value)}
                    style={{
                      width: '100%', paddingRight: 42, boxSizing: 'border-box',
                      borderColor: pwMismatch ? '#ef4444' : pwMatch ? '#22c55e' : undefined,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    style={{
                      position: 'absolute', right: 12,
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--rh-text-muted)', display: 'flex', alignItems: 'center', padding: 0, lineHeight: 1,
                    }}
                    tabIndex={-1}
                    aria-label={showConfirm ? 'Hide' : 'Show'}
                  >
                    <EyeIcon open={showConfirm} />
                  </button>
                </div>
                {pwMismatch && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#ef4444' }}>Passwords do not match</p>}
                {pwMatch && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#16a34a' }}>Passwords match ✓</p>}
              </div>

              <button type="submit" className="rh-btn-primary" disabled={busy}>
                {busy ? 'Creating account…' : 'Create Account'}
              </button>
            </form>
          </>
        )}

        {done && (
          <div style={{ textAlign: 'center', padding: '12px 0 20px' }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <svg width="26" height="26" fill="none" stroke="#16a34a" strokeWidth="2.5" viewBox="0 0 24 24">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <p style={{ margin: '0 0 6px', fontWeight: 600, color: 'var(--rh-text)' }}>Account created!</p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--rh-text-muted)' }}>Redirecting to sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
